import type { ComponentChildren, RefObject } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { useUser } from '#hooks/UNSAFE_useUser';
import { getClientHints } from '#utils/PRIVATE_clientHints';

/**
 * A hook to manage focus for readonly radio elements within a container. In Safari,
 * the manager will not wrap focus from first and last element when navigating with arrow keys,
 * while other browsers will. While in Safari and RTL mode, it will also match the behavior of
 * useCollectionFocusRing by not reversing the left/right arrow key behavior.
 *
 * Manager will automatically reset whenever containerRef or children change. However, it will
 * retain the current focus index if it does not exceed the number of focusable elements.
 * In that case, it will reset to the last element.
 *
 * @param containerRef - A ref to the container element that holds the radio elements.
 * @param children - The children within the container.
 * @returns An object with methods to manage focus among the radio elements.
 */
const useRadioFocusManager = (
  containerRef: RefObject<HTMLDivElement | null>,
  children: ComponentChildren
) => {
  // array of focusable elements
  const focusableElementsRef = useRef<HTMLElement[]>([]);

  // current index
  const focusIndexRef = useRef(0);

  // determine if the direction is RTL for arrow key handling
  const isRtl = useUser().direction === 'rtl';
  const isSafari = getClientHints().browser === 'safari';

  // move focus to the next radio element
  const focusNext = useCallback(() => {
    const elements = focusableElementsRef.current;
    if (!elements.length) return;

    const curr = focusIndexRef.current;
    // clamp on Safari to match arrow key behavior in useCollectionFocusRing, wrap elsewhere
    const nextIndex = isSafari
      ? Math.min(curr + 1, elements.length - 1)
      : (curr + 1) % elements.length;

    focusIndexRef.current = nextIndex;
    elements[nextIndex]?.focus();
  }, [isSafari]);

  // move focus to the previous radio element
  const focusPrevious = useCallback(() => {
    const elements = focusableElementsRef.current;
    if (!elements.length) return;

    const curr = focusIndexRef.current;
    // clamp on Safari to match arrow key behavior in useCollectionFocusRing, wrap elsewhere
    const prevIndex = isSafari
      ? Math.max(curr - 1, 0)
      : (curr - 1 + elements.length) % elements.length;

    focusIndexRef.current = prevIndex;
    elements[prevIndex]?.focus();
  }, [isSafari]);

  // explicitly set the focus index
  const setFocusIndex = useCallback((index: number) => {
    focusIndexRef.current = index;
  }, []);

  // reset focus to the element indicated by the current index, mainly for testing purposes
  const resetFocus = useCallback(() => {
    const elements = focusableElementsRef.current;
    if (!elements.length) return;
    elements[focusIndexRef.current].focus();
  }, []);

  // handle ArrowUp/Down/Left/Right key presses to navigate radios
  const handleArrowKeyPress = useCallback(
    (event: KeyboardEvent) => {
      const { key } = event;

      const handler: Record<string, (() => void) | undefined> = {
        ArrowDown: focusNext,
        ArrowUp: focusPrevious,
        ArrowRight: isRtl && !isSafari ? focusPrevious : focusNext,
        ArrowLeft: isRtl && !isSafari ? focusNext : focusPrevious
      };

      const action = handler[key];
      if (action) {
        event.preventDefault();
        action();
      }
    },
    [focusNext, focusPrevious, isRtl, isSafari]
  );

  const resetManager = useCallback(() => {
    if (!containerRef.current) return;
    const elements = containerRef.current.querySelectorAll(
      '[data-checked]'
    ) as NodeListOf<HTMLElement>;
    focusableElementsRef.current = Array.from(elements);

    // reset index to last item if previous index is greater than the new items
    if (focusIndexRef.current >= focusableElementsRef.current.length) {
      setFocusIndex(focusableElementsRef.current.length - 1);
    }
  }, [containerRef, setFocusIndex]);

  // reset the manager whenever children change
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;
    resetManager();
  }, [resetManager, children, containerRef]);

  return {
    focusNext,
    focusPrevious,
    resetFocus,
    setFocusIndex,
    focusIndex: focusIndexRef.current,
    handleArrowKeyPress
  };
};

export { useRadioFocusManager };
